import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { gmailCall, sentMailSummary, syncAllSentMail } from "./gmailApi";

type GmailStatus = {
  ok: true;
  connected: boolean;
  google_email: string | null;
};

export const GmailConnect = () => {
  const [status, setStatus] = useState<GmailStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    gmailCall<GmailStatus>({ action: "status" })
      .then((result) => {
        if (!cancelled) setStatus(result);
      })
      .catch(() => {
        if (!cancelled) {
          setStatus({ ok: true, connected: false, google_email: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await gmailCall<{ ok: true; url: string }>({
        action: "start",
      });
      window.location.assign(result.url);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Google sign-in could not start.",
      );
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await syncAllSentMail();
      setMessage(sentMailSummary(result));
      setStatus(await gmailCall<GmailStatus>({ action: "status" }));
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Sent mail could not be read.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card id="gmail">
      <CardContent className="space-y-3">
        <h2 className="text-xl font-semibold text-muted-foreground">Gmail</h2>
        <p className="text-sm text-muted-foreground">
          Sign in with the Gmail account that already sent the follow-ups.
          Google asks which account to use. Vogelkop reads that mailbox only and
          copies each sent message onto the lead with the same email address.
        </p>
        {status?.connected && status.google_email ? (
          <p className="text-sm">
            Reading sent mail from {status.google_email}.
          </p>
        ) : (
          <p className="text-sm">No Gmail account is connected.</p>
        )}
        {message ? <p className="text-sm">{message}</p> : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={signIn} disabled={busy}>
            Sign in with Google
          </Button>
          {status?.connected ? (
            <Button
              type="button"
              variant="outline"
              onClick={sync}
              disabled={busy}
            >
              Sync sent mail
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
